"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Calendar, CircleCheck as CheckCircle2, Compass, FileSearch, ShieldCheck, TrendingUp, UserCheck } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const valuePoints = [{
  id: 1,
  title: "No long-term contracts",
  iconName: "shield"
}, {
  id: 2,
  title: "Transparent pricing",
  iconName: "user"
}, {
  id: 3,
  title: "Strategic leadership",
  iconName: "compass"
}, {
  id: 4,
  title: "Revenue-focused SEO",
  iconName: "trending"
}, {
  id: 5,
  title: "Flexible engagement",
  iconName: "check"
}];
export default function Conversion() {
  const shouldReduceMotion = useReducedMotion();
  const getIcon = (iconName: string) => {
    switch (iconName) {
      case "shield":
        return <ShieldCheck className="size-5 text-primary" />;
      case "user":
        return <UserCheck className="size-5 text-primary" />;
      case "compass":
        return <Compass className="size-5 text-primary" />;
      case "trending":
        return <TrendingUp className="size-5 text-primary" />;
      case "check":
      default:
        return <CheckCircle2 className="size-5 text-primary" />;
    }
  };
  return <section id="finalconversioncta" data-nav="light" className="relative overflow-hidden bg-background py-20 md:py-28 lg:py-32">
      {/* Decorative subtle background accents */}
      <div aria-hidden="true" className="pointer-events-none absolute -top-24 -left-24 h-96 w-96 rounded-full bg-secondary/40 blur-3xl" />
      <div aria-hidden="true" className="pointer-events-none absolute -bottom-24 -right-24 h-96 w-96 rounded-full bg-primary/5 blur-3xl" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl relative z-10">
        {/* Section Header */}
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        ease: "easeOut"
      }} className="mx-auto max-w-3xl text-center">
          <Badge variant="secondary" className="mb-4 px-3.5 py-1 text-xs font-medium tracking-wide">
            Personal Expert Consultation
          </Badge>
          
          <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground text-balance">
            Get Started with Affordable SEO Services Today
          </h2>

          <p className="mt-6 text-base sm:text-lg leading-relaxed text-muted-foreground font-sans">
            If you're ready to stop overpaying for generic agency strategies and start investing in SEO that's built around your business, your budget, and your goals, the next step is simple. I offer every prospective client a free, no-obligation consultation where I'll assess your current online visibility, identify the biggest opportunities for improvement, and explain clearly what it would take to achieve your objectives.
          </p>
        </motion.div>

        {/* Dual Primary CTA Cards */}
        <div className="mt-12 lg:mt-16 grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {/* Card 1: Free Consultation */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 24
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.1,
          ease: "easeOut"
        }} className="h-full">
            <Card className="h-full border border-border bg-card shadow-md flex flex-col justify-between hover:border-primary/40 transition-[border-color,box-shadow] duration-300">
              <CardHeader className="space-y-3 pb-4">
                <div className="inline-flex size-12 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <SafeHydrate><Calendar className="size-6" /></SafeHydrate>
                </div>
                <CardTitle className="font-serif text-2xl font-bold text-foreground">
                  Book a Free SEO Consultation
                </CardTitle>
                <CardDescription className="text-base text-muted-foreground font-sans leading-relaxed">
                  Let's discuss your business, your market, and the most effective path to sustainable growth through search.
                </CardDescription>
              </CardHeader>
              
              <CardContent className="pt-0">
                <ul className="space-y-2 text-sm text-foreground">
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="size-4 text-primary shrink-0" />
                    <span>30-minute direct video or phone call</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="size-4 text-primary shrink-0" />
                    <span>Clear strategic overview tailored to your market</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="size-4 text-primary shrink-0" />
                    <span>Zero sales pressure or lock-in commitment</span>
                  </li>
                </ul>
              </CardContent>

              <CardFooter className="pt-2">
                <Button asChild size="lg" className="w-full justify-center gap-2 font-medium shadow-sm">
                  <Link to="/ContactPage">
                    <span>Book Your Free Consultation</span>
                    <ArrowRight className="size-4" />
                  </Link>
                </Button>
              </CardFooter>
            </Card>
          </motion.div>

          {/* Card 2: Free SEO Audit */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 24
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.2,
          ease: "easeOut"
        }} className="h-full">
            <Card className="h-full border border-border bg-card shadow-md flex flex-col justify-between hover:border-primary/40 transition-[border-color,box-shadow] duration-300">
              <CardHeader className="space-y-3 pb-4">
                <div className="inline-flex size-12 items-center justify-center rounded-lg bg-accent/15 text-accent-foreground">
                  <FileSearch className="size-6 text-foreground" />
                </div>
                <CardTitle className="font-serif text-2xl font-bold text-foreground">
                  Get a Free SEO Audit
                </CardTitle>
                <CardDescription className="text-base text-muted-foreground font-sans leading-relaxed">
                  Receive a clear, honest assessment of your website's current SEO performance, technical health, and opportunities, with no obligation to proceed.
                </CardDescription>
              </CardHeader>

              <CardContent className="pt-0">
                <ul className="space-y-2 text-sm text-foreground">
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="size-4 text-primary shrink-0" />
                    <span>Review of technical errors & crawling health</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="size-4 text-primary shrink-0" />
                    <span>Keyword ranking & visibility gap analysis</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="size-4 text-primary shrink-0" />
                    <span>Prioritised list of actionable quick wins</span>
                  </li>
                </ul>
              </CardContent>

              <CardFooter className="pt-2">
                <Button asChild variant="outline" size="lg" className="w-full justify-center gap-2 font-medium border-border hover:bg-secondary">
                  <Link to="/SeoAuditsPage">
                    <span>Request a Free SEO Audit</span>
                    <ArrowRight className="size-4" />
                  </Link>
                </Button>
              </CardFooter>
            </Card>
          </motion.div>
        </div>

        {/* Reinforcement Value Strip */}
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1
      } : {
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        delay: 0.3
      }} className="mt-14 rounded-xl border border-border bg-card/60 p-6 md:p-8 backdrop-blur-xs">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-6 text-center">
            {valuePoints.map((item, i) => <div key={item.id} data-index={i} className="flex flex-col items-center justify-center space-y-2.5 p-2">
                <div className="flex size-10 items-center justify-center rounded-full bg-primary/10">
                  {getIcon(item.iconName)}
                </div>
                <span className="text-sm font-semibold text-foreground font-sans">
                  {item.title}
                </span>
              </div>)}
          </div>
        </motion.div>

        {/* Closing Pitch & Contextual Links */}
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        delay: 0.4,
        ease: "easeOut"
      }} className="mt-12 text-center max-w-3xl mx-auto space-y-6">
          <p className="text-sm sm:text-base leading-relaxed text-muted-foreground font-sans">
            No high-pressure sales. No inflated promises. Just honest, professional SEO advice from an experienced independent UK SEO consultant who genuinely wants to help your business grow. Whether you're a local service provider looking to improve local search visibility, an ecommerce store wanting more traffic without more paid advertising, or a B2B company seeking quality lead generation – I can help.
          </p>

          <Separator className="max-w-xs mx-auto" />

          <div className="flex flex-wrap items-center justify-center gap-6 pt-2 text-sm font-medium">
            <Link to="/AboutMePage" className="inline-flex items-center gap-1.5 text-foreground hover:text-primary transition-colors underline-offset-4 hover:underline">
              <span>Learn more about my background</span>
              <ArrowRight className="size-3.5" />
            </Link>

            <span className="text-border hidden sm:inline" aria-hidden="true">•</span>

            <Link to="/ContactPage" className="inline-flex items-center gap-1.5 text-foreground hover:text-primary transition-colors underline-offset-4 hover:underline">
              <span>Get in touch directly</span>
              <ArrowRight className="size-3.5" />
            </Link>
          </div>
        </motion.div>
      </div>
    </section>;
}